import { TickLabelProps } from '../../../types/text-props.js';
import { TickPrimitiveValue } from '../../../types/tick-value.js';
import { getTickValueAsNumber } from '../../../utils/get-tick-value-as-number.js';
import {
  TICK_ALIGMENT,
  X_AXES_TICK_LINE_OFFSET,
  Y_AXES_TICK_LINE_OFFSET,
  type TickAligmentMode,
} from '../constants.js';
import { Orientation, TickLabelLayout } from '../types.js';

/*
 * x and y represent the offset from ORIGIN point of the axis.
 * This means that axis line width wouldn't count for it
 */
const VISX_BOTTOM_OFFSET = 2;
// ADES-6688 workaround suggested to solve the truncation issue
// comment related: https://dt-rnd.atlassian.net/browse/ADES-6688?focusedCommentId=3205215
export const ELLIPSIS_BUFFER = 2;

type TickLabelAligment = {
  textAnchor: TickAligmentMode;
  verticalAnchor: TickAligmentMode;
};

const leftTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  x: -Y_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.End,
  verticalAnchor:
    index === 0
      ? TICK_ALIGMENT.End
      : index === values.length - 1
      ? TICK_ALIGMENT.Start
      : TICK_ALIGMENT.Middle,
});

const rightTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  x: +Y_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.Start,
  verticalAnchor:
    index === 0
      ? TICK_ALIGMENT.End
      : index === values.length - 1
      ? TICK_ALIGMENT.Start
      : TICK_ALIGMENT.Middle,
});

const leftTickCategoricalLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
) => ({
  x: -Y_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.End,
  verticalAnchor: TICK_ALIGMENT.Middle,
});

const topTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  y: -X_AXES_TICK_LINE_OFFSET,
  textAnchor:
    index === 0
      ? TICK_ALIGMENT.Start
      : index === values.length - 1
      ? TICK_ALIGMENT.End
      : TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.End,
});

const topRotatedTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor:
    index === 0
      ? TICK_ALIGMENT.Start
      : index === values.length - 1
      ? TICK_ALIGMENT.End
      : TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.Middle,
  angle: -90,
});

const topTickLabelPropsForCategoryAxis: TickLabelProps<TickPrimitiveValue> = (
  _value,
) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.End,
});

const topRotatedTickLabelPropsForCategoryAxis: TickLabelProps<
  TickPrimitiveValue
> = (_value) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.End,
  verticalAnchor: TICK_ALIGMENT.Middle,
  angle: -90,
});

const bottomTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor:
    index === 0
      ? TICK_ALIGMENT.Start
      : index === values.length - 1
      ? TICK_ALIGMENT.End
      : TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.Start,
});

const bottomRotatedTickLabelPropsForCategoryAxis: TickLabelProps<
  TickPrimitiveValue
> = (_value) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.End,
  verticalAnchor: TICK_ALIGMENT.Middle,
  angle: -90,
});

const bottomRotatedTickLabelProps: TickLabelProps<TickPrimitiveValue> = (
  _value,
  index,
  values,
) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor:
    index === 0
      ? TICK_ALIGMENT.Start
      : index === values.length - 1
      ? TICK_ALIGMENT.End
      : TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.Middle,
  angle: -90,
});

const bottomTickLabelPropsForCategoryAxis: TickLabelProps<
  TickPrimitiveValue
> = (_value) => ({
  y: X_AXES_TICK_LINE_OFFSET,
  textAnchor: TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.Start,
});

export const getTickLabelPropsForCategoryAxis = (
  orientation: Orientation,
  tickLabelLayout: TickLabelLayout = 'horizontal',
) => {
  if (orientation === 'left') {
    return leftTickCategoricalLabelProps;
  }

  if (orientation === 'top') {
    return tickLabelLayout === 'vertical'
      ? topRotatedTickLabelPropsForCategoryAxis
      : topTickLabelPropsForCategoryAxis;
  }

  return tickLabelLayout === 'vertical'
    ? bottomRotatedTickLabelPropsForCategoryAxis
    : bottomTickLabelPropsForCategoryAxis;
};

export const bottomTickLabelDateProps: TickLabelProps<Date> = (_value) => ({
  y: X_AXES_TICK_LINE_OFFSET + VISX_BOTTOM_OFFSET,
  textAnchor: TICK_ALIGMENT.Middle,
  verticalAnchor: TICK_ALIGMENT.Start,
});

export const bottomPanTickLabelDateProps: TickLabelProps<Date> = (
  _value,
  index,
  values,
) => {
  const result: TickLabelAligment = {
    textAnchor: TICK_ALIGMENT.Middle,
    verticalAnchor: TICK_ALIGMENT.Middle,
  };

  const isFirstItem = index === 0;
  const isLastItem = index === values.length - 1;

  if (isFirstItem) {
    result.textAnchor = TICK_ALIGMENT.Start;
  }

  if (isLastItem) {
    result.textAnchor = TICK_ALIGMENT.End;
  }

  return result;
};

export const getTickLabelPropsForValueAxis = (
  orientation: Orientation,
  tickLabelLayout: TickLabelLayout = 'horizontal',
) => {
  if (orientation === 'left') {
    return leftTickLabelProps;
  }

  if (orientation === 'right') {
    return rightTickLabelProps;
  }

  if (orientation === 'top') {
    return tickLabelLayout === 'vertical'
      ? topRotatedTickLabelProps
      : topTickLabelProps;
  }

  return tickLabelLayout === 'vertical'
    ? bottomRotatedTickLabelProps
    : bottomTickLabelProps;
};

/**
 * Gets the corresponding props for a tick label.
 * Takes index and whether the tick is placed at data-min/data-max into consideration.
 *
 * @param min - data-min
 * @param max -  data-max
 * @returns props for the tick label
 */
export const getTickLabelPropsForGanttAxis = (
  min?: number,
  max?: number,
): TickLabelProps<TickPrimitiveValue> => {
  return (value, index, values) => {
    let textAnchor: TickAligmentMode = TICK_ALIGMENT.Middle;
    let dx = 0;
    const valueAsNumber = getTickValueAsNumber(value);

    if (values.length === 1 && valueAsNumber && min && max) {
      textAnchor =
        valueAsNumber - min < valueAsNumber - max
          ? TICK_ALIGMENT.Start
          : TICK_ALIGMENT.End;
    } else if (index === 0) {
      textAnchor = TICK_ALIGMENT.Start;
      dx = valueAsNumber === min ? 4 : 0;
    } else if (index === values.length - 1) {
      textAnchor = TICK_ALIGMENT.End;
      dx = valueAsNumber === max ? -4 : 0;
    }

    return {
      dx,
      dy: -2,
      textAnchor,
      verticalAnchor: TICK_ALIGMENT.Middle,
    };
  };
};
